import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { Building2, Users, Swords, Loader2, ExternalLink, AlertTriangle, Play, Crosshair, SlidersHorizontal } from "lucide-react";
import { runBusinessIntel, type BusinessIntel } from "@/lib/intel/feeds.functions";
import { getPaddleEnvironment } from "@/lib/paddle";
import { SeverityBadge } from "@/components/intel/SeverityBadge";
import { OrgDossierPanel } from "@/components/intel/OrgDossierPanel";

type Focus = "overview" | "competitors" | "people" | "ownership";

const FOCUS: Array<[Focus, string]> = [
  ["overview", "Overview"],
  ["competitors", "Competitors"],
  ["people", "Chairman & key people"],
  ["ownership", "Ownership / UBO"],
];

const OWNERSHIP_TYPES = ["any", "public / listed", "private", "state-owned", "PE / VC backed", "family-owned", "subsidiary", "non-profit"];

const ROLES = ["Chairman", "Board member", "CEO", "CFO", "COO", "CTO", "Founder", "Beneficial owner", "Major shareholder"];

export function BusinessIntelPanel({
  company,
  onPivot,
}: {
  company: string;
  onPivot?: (target: string) => void;
}) {
  const run = useServerFn(runBusinessIntel);
  const [name, setName] = useState(company);
  const [mode, setMode] = useState<"intel" | "dossier">("intel");
  const [focus, setFocus] = useState<Focus>("overview");
  const [data, setData] = useState<BusinessIntel | null>(null);
  const [industry, setIndustry] = useState("");
  const [geography, setGeography] = useState("");
  const [ownership, setOwnership] = useState("any");
  const [roles, setRoles] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const activeFilters =
    (industry.trim() ? 1 : 0) + (geography.trim() ? 1 : 0) + (ownership !== "any" ? 1 : 0) + (roles.length ? 1 : 0);

  const m = useMutation({
    mutationFn: async () =>
      run({
        data: {
          company: name.trim(),
          focus,
          environment: getPaddleEnvironment(),
          ...(industry.trim() ? { industry: industry.trim() } : {}),
          ...(geography.trim() ? { geography: geography.trim() } : {}),
          ...(ownership !== "any" ? { ownership } : {}),
          ...(roles.length ? { roles } : {}),
        },
      }),
    onSuccess: (d) => setData(d),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1">
        {([["intel", "Business intel"], ["dossier", "Organization dossier"]] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setMode(k)}
            className={`rounded-md border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
              mode === k ? "border-cyan/50 bg-cyan/15 text-cyan" : "border-panel-border bg-background/40 text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "dossier" ? (
        <OrgDossierPanel company={company} {...(onPivot ? { onPivot } : {})} />
      ) : (
      <>
      <div className="glass rounded-lg p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex w-full flex-1 items-center gap-2 rounded-md border border-panel-border bg-background/40 px-3 py-2 sm:w-auto sm:min-w-[220px]">
            <Building2 className="h-4 w-4 text-cyan" />
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && m.mutate()}
              placeholder="Company name or domain…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <button
            onClick={() => name.trim() && m.mutate()}
            disabled={m.isPending || !name.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
          >
            {m.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {m.isPending ? "Researching…" : "Run business intel"}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {FOCUS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFocus(k)}
              className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] ${
                focus === k ? "border-cyan/50 bg-cyan/15 text-cyan" : "border-panel-border bg-background/40 text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setShowFilters((s) => !s)}
            className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-mono text-[10px] ${
              activeFilters ? "border-cyan/50 bg-cyan/15 text-cyan" : "border-panel-border bg-background/40 text-muted-foreground hover:text-foreground"
            }`}
          >
            <SlidersHorizontal className="h-3 w-3" /> Filters{activeFilters ? ` · ${activeFilters}` : ""}
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 space-y-3 rounded-md border border-panel-border bg-background/40 p-3">
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Industry / sector</span>
                <input
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g. fintech, defence, logistics"
                  className="mt-1 w-full rounded-md border border-panel-border bg-background/60 px-2.5 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-cyan/50"
                />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Geography</span>
                <input
                  value={geography}
                  onChange={(e) => setGeography(e.target.value)}
                  placeholder="e.g. Finland, EU, UAE"
                  className="mt-1 w-full rounded-md border border-panel-border bg-background/60 px-2.5 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-cyan/50"
                />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ownership / UBO</span>
                <select
                  value={ownership}
                  onChange={(e) => setOwnership(e.target.value)}
                  className="mt-1 w-full rounded-md border border-panel-border bg-background/60 px-2.5 py-1.5 text-sm outline-none focus:border-cyan/50"
                >
                  {OWNERSHIP_TYPES.map((o) => (
                    <option key={o} value={o}>{o === "any" ? "Any structure" : o}</option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Board / C-suite roles</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {ROLES.map((r) => {
                  const on = roles.includes(r);
                  return (
                    <button
                      key={r}
                      onClick={() => setRoles((prev) => (on ? prev.filter((x) => x !== r) : [...prev, r]))}
                      className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] ${
                        on ? "border-cyan/50 bg-cyan/15 text-cyan" : "border-panel-border bg-background/40 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>
            {activeFilters > 0 && (
              <button
                onClick={() => { setIndustry(""); setGeography(""); setOwnership("any"); setRoles([]); }}
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
        {m.isError && (
          <div className="mt-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
            {m.error instanceof Error ? m.error.message : "Business intel failed"}
          </div>
        )}
      </div>

      {!data && !m.isPending && (
        <div className="rounded-md border border-panel-border bg-background/40 p-6 text-center text-sm text-muted-foreground">
          Research a business: leadership (chairman, board, C-suite), ownership, and the competitive set with their key people.
        </div>
      )}

      {data && (
        <>
          <div className="glass rounded-lg p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Company profile</div>
            <div className="mt-1 font-display text-xl">{data.company.legal_name}</div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{data.company.summary || "No summary returned."}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {([
                ["HQ", data.company.hq],
                ["Founded", data.company.founded],
                ["Industry", data.company.industry],
                ["Size", data.company.size],
                ["Ownership", data.company.ownership],
              ] as Array<[string, string]>).filter(([, v]) => v).map(([l, v]) => (
                <div key={l} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                  <div className="mt-0.5 text-sm">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {data.key_people.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
                <Users className="h-3 w-3" /> Chairman & key people · {data.key_people.length}
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {data.key_people.map((p) => (
                  <div key={p.name + p.role} className="rounded-md border border-panel-border bg-background/40 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{p.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{p.role}</div>
                      </div>
                      {onPivot && (
                        <button
                          onClick={() => onPivot(p.name)}
                          title={`Investigate ${p.name}`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-cyan/40 bg-cyan/10 px-2 py-1 font-mono text-[10px] text-cyan hover:bg-cyan/20"
                        >
                          <Crosshair className="h-3 w-3" /> Investigate
                        </button>
                      )}
                    </div>
                    {p.background && <div className="mt-2 text-xs text-muted-foreground">{p.background}</div>}
                    {p.risk_notes && (
                      <div className="mt-1.5 flex gap-1.5 text-xs text-amber">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {p.risk_notes}
                      </div>
                    )}
                    <SourceLinks urls={p.sources} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.competitors.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
                <Swords className="h-3 w-3" /> Competitive set · {data.competitors.length}
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {data.competitors.map((k) => (
                  <div key={k.name} className="rounded-md border border-panel-border bg-background/40 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{k.name}</div>
                        {k.domain && <div className="font-mono text-[10px] text-muted-foreground">{k.domain}</div>}
                      </div>
                      {onPivot && (
                        <button
                          onClick={() => onPivot(k.domain || k.name)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-cyan/40 bg-cyan/10 px-2 py-1 font-mono text-[10px] text-cyan hover:bg-cyan/20"
                        >
                          <Crosshair className="h-3 w-3" /> Scan
                        </button>
                      )}
                    </div>
                    {k.positioning && <div className="mt-2 text-xs text-muted-foreground">{k.positioning}</div>}
                    {k.relative_strength && <div className="mt-1 text-xs">{k.relative_strength}</div>}
                    {k.key_people.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {k.key_people.map((p) => (
                          <button
                            key={p}
                            onClick={() => onPivot?.(p.split("—")[0]!.trim())}
                            className="rounded-full border border-panel-border bg-panel/40 px-2 py-0.5 font-mono text-[10px] text-muted-foreground hover:text-foreground hover:border-cyan/40"
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                    <SourceLinks urls={k.sources} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.risk_flags.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Risk flags</div>
              <div className="mt-3 space-y-2">
                {data.risk_flags.map((r) => (
                  <div key={r.label} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                    <div className="flex items-center gap-2">
                      <SeverityBadge s={r.severity} />
                      <span className="text-sm font-medium">{r.label}</span>
                    </div>
                    {r.detail && <div className="mt-1 text-xs text-muted-foreground">{r.detail}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.opportunities.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Observations</div>
              <ul className="mt-2 space-y-1.5 text-sm">
                {data.opportunities.map((o) => (
                  <li key={o} className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.citations.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Sources</div>
              <div className="mt-2 space-y-1">
                {data.citations.map((c) => (
                  <a key={c} href={c} target="_blank" rel="noreferrer noopener"
                    className="flex items-center gap-1.5 truncate font-mono text-[11px] text-muted-foreground hover:text-cyan">
                    <ExternalLink className="h-3 w-3 shrink-0" /> {c}
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
}

function SourceLinks({ urls }: { urls: string[] }) {
  if (!urls.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {urls.map((u, i) => (
        <a key={u} href={u} target="_blank" rel="noreferrer noopener"
          className="font-mono text-[10px] text-muted-foreground hover:text-cyan">
          [{i + 1}]
        </a>
      ))}
    </div>
  );
}